import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Message, Model } from '../types/chat';
import ModelSelector from './ModelSelector';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { fetchAvailableModels, streamChatMessage } from '../services/chatService';
import Sidebar from './Sidebar';
import { useConversationStore, useCurrentConversation } from '../stores/useConversationStore';
import { chatHistoryService } from '../services/chatHistoryService';

const ChatInterface: React.FC = () => {

  // État local pour l'interface (temporaire pendant le streaming)
  const [messages, setMessages] = useState<Message[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [modelsLoading, setModelsLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentStreamingContentRef = useRef<string>('');

  // État des conversations depuis le store
  const currentConversation = useCurrentConversation();
  const {
    currentMessages
  } = useConversationStore();

  // Actions directes depuis le store pour éviter les re-renders infinis
  const storeActions = useConversationStore.getState();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (streamingContent) {
      scrollToBottom();
    }
  }, [streamingContent]);

  // Synchroniser les messages depuis le store avec l'état local
  useEffect(() => {
    if (currentMessages.length > 0 && !isStreaming) {
      const convertedMessages: Message[] = currentMessages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at)
      }));
      setMessages(convertedMessages);
    } else if (currentMessages.length === 0 && !isStreaming) {
      setMessages([]);
    }
  }, [currentMessages, isStreaming]);

  // Charger les modèles disponibles
  useEffect(() => {
    const loadModels = async () => {
      try {
        const availableModels = await fetchAvailableModels();
        setModels(availableModels);
        // Sélectionner automatiquement le premier modèle
        if (availableModels.length > 0 && !selectedModel) {
          setSelectedModel(availableModels[0]);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
      } finally {
        setModelsLoading(false);
      }
    };

    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Uniquement au montage du composant

  // Synchroniser le modèle sélectionné avec la conversation courante
  useEffect(() => {
    if (currentConversation && models.length > 0) {
      // Chercher le modèle correspondant à la conversation
      const conversationModel = models.find(model =>
        model.id === currentConversation.model_id
      );

      if (conversationModel && selectedModel?.id !== conversationModel.id) {
        console.log('Switching model to match conversation:', conversationModel.id);
        setSelectedModel(conversationModel);
      }
    }
  }, [currentConversation, models, selectedModel]);

  // Charger les conversations au démarrage
  useEffect(() => {
    const initializeConversations = async () => {
      try {
        // Vérifier la disponibilité du chat backend
        await chatHistoryService.healthCheck();
        await storeActions.loadConversations();
      } catch (error) {
        console.warn('Chat backend not available, continuing without history:', error);
      }
    };

    initializeConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Uniquement au montage du composant

  // Fonction pour démarrer une nouvelle conversation (utilisée par le bouton New dans Sidebar)
  // const handleNewConversation = () => {
  //   setMessages([]);
  //   setStreamingContent('');
  //   currentStreamingContentRef.current = '';
  //   setIsStreaming(false);
  //   useConversationStore.setState({ currentConversationId: null, currentMessages: [] });
  // };

  const handleSendMessage = async (content: string) => {
    if (!selectedModel) {
      alert('Please select a model first');
      return;
    }

    let conversationId = currentConversation?.id;

    try {
      // Si c'est la première conversation ou pas de conversation active, créer une nouvelle
      if (!conversationId) {
        const title = chatHistoryService.generateConversationTitle(content);
        conversationId = await storeActions.createConversation(title, selectedModel.id, selectedModel.name || selectedModel.id);
      }

      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content,
        timestamp: new Date()
      };

      // Ajouter le message utilisateur à l'état local temporairement
      setMessages(prev => [...prev, userMessage]);
      setIsStreaming(true);
      setStreamingContent('');
      currentStreamingContentRef.current = '';

      // Sauvegarder le message utilisateur dans l'historique
      await storeActions.addMessage(conversationId, 'user', content);

      // Créer le générateur de streaming avec tout l'historique de la conversation
      const conversationHistory = [...messages, userMessage]; // Inclure le nouveau message utilisateur
      const streamGenerator = streamChatMessage(conversationHistory, selectedModel.id);

      // Consommer le générateur comme dans O365-addins
      for await (const chunk of streamGenerator) {
        console.log('Streaming chunk received:', chunk);
        currentStreamingContentRef.current += chunk;

        // Forcer la mise à jour immédiate avec flushSync
        flushSync(() => {
          setStreamingContent(currentStreamingContentRef.current);
        });

        console.log('Updated streaming content:', currentStreamingContentRef.current);

        // Petit délai pour éviter que les chunks arrivent trop vite
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Quand le streaming est terminé, ajouter le message final
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: currentStreamingContentRef.current,
        timestamp: new Date()
      };

      // Ajouter temporairement à l'état local
      setMessages(prev => [...prev, assistantMessage]);

      // Sauvegarder le message assistant dans l'historique
      await storeActions.addMessage(conversationId, 'assistant', currentStreamingContentRef.current, {
        model_id: selectedModel.id,
        model_name: selectedModel.name || selectedModel.id,
        timestamp: new Date().toISOString()
      });

      setIsStreaming(false);
      setStreamingContent('');
      currentStreamingContentRef.current = '';

    } catch (error) {
      console.error('Failed to stream message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, there was an error processing your message.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsStreaming(false);
      setStreamingContent('');
      currentStreamingContentRef.current = '';
    }
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      backgroundColor: '#f8f9fa'
    }}>
      {/* Sidebar */}
      <Sidebar />

      {/* Main content area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Model selector moved to main content */}
        <ModelSelector
          models={models}
          selectedModel={selectedModel}
          onModelSelect={setSelectedModel}
          isLoading={modelsLoading}
        />

        {/* Chat messages area */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '1rem',
          backgroundColor: '#f8f9fa'
        }}>
        {messages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#666',
            fontSize: '16px',
            marginTop: '2rem'
          }}>
            {currentConversation ? (
              selectedModel ?
                `Continue your conversation with ${selectedModel.name}` :
                'Select a model to continue chatting'
            ) : (
              selectedModel ?
                `Start a new conversation with ${selectedModel.name}` :
                'Select a model to start chatting'
            )}
            {currentConversation && (
              <div style={{
                marginTop: '1rem',
                fontSize: '14px',
                color: '#888'
              }}>
                Current conversation: {currentConversation.title}
              </div>
            )}
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isStreaming && streamingContent && (
              <ChatMessage
                key="streaming"
                message={{
                  id: 'streaming',
                  role: 'assistant',
                  content: streamingContent,
                  timestamp: new Date()
                }}
                isStreaming={true}
              />
            )}
          </>
        )}
        {isStreaming && !streamingContent && (
          <div style={{
            textAlign: 'center',
            color: '#666',
            fontSize: '14px',
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #0078d4',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Generating response...
          </div>
        )}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat input at bottom */}
        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={!selectedModel || isStreaming}
        />
      </div>
    </div>
  );
};

export default ChatInterface;